/** Due diligence section (ESG info, certifications, insurance, contract & renewal) for a
 * supplier's governance profile. Extracted out of GovernanceTab so the same section can be
 * reused as a step in the Supplier Onboarding wizard (Demo Mode only, local-only - no
 * backend persistence yet). See `src/mocks/data/supplierGovernanceData.ts`. */

import {
  Box,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../../components/ui/Section';
import {
  DocumentStatus,
  RenewalStatus,
  SupplierGovernanceProfile,
} from '../../../../../mocks/data/supplierGovernanceData';

const DOCUMENT_STATUS_COLOR: Record<DocumentStatus, 'success' | 'warning' | 'error'> = {
  Valid: 'success',
  ExpiringSoon: 'warning',
  Expired: 'error',
};

const RENEWAL_STATUS_COLOR: Record<RenewalStatus, 'success' | 'warning' | 'error'> = {
  OnTrack: 'success',
  RenewalDueSoon: 'warning',
  Overdue: 'error',
};

export interface DueDiligenceSectionProps {
  profile: SupplierGovernanceProfile;
}

const DueDiligenceSection: React.FC<DueDiligenceSectionProps> = ({ profile }) => {
  const { t } = useTranslation('suppliers');

  return (
    <>
      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('esgInfo', 'ESG info')}
          </Typography>
          <Grid container
            spacing={2}>
            <Grid item
              xs={12}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('carbonRatingScore', 'Carbon rating score')}</Typography>
              <Typography variant="textSM">{profile.esg.carbonRatingScore ?? t('notCaptured', 'Not captured')}</Typography>
            </Grid>
            <Grid item
              xs={12}
              md={8}>
              <Typography variant="textXS"
                color="text.secondary">{t('sustainabilityAccreditations', 'Sustainability accreditations')}</Typography>
              <Stack direction="row"
                spacing={1}
                sx={{ mt: 1, flexWrap: 'wrap' }}>
                {profile.esg.sustainabilityAccreditations.length > 0
                  ? profile.esg.sustainabilityAccreditations.map((accreditation) => (
                    <Chip key={accreditation}
                      label={accreditation}
                      size="small" />
                  ))
                  : <Typography variant="textSM">{t('notCaptured', 'Not captured')}</Typography>}
              </Stack>
            </Grid>
            <Grid item
              xs={12}>
              <Typography variant="textXS"
                color="text.secondary">{t('ethicalSourcingStatement', 'Ethical sourcing statement')}</Typography>
              <Typography variant="textSM">{profile.esg.ethicalSourcingStatement ?? t('notCaptured', 'Not captured')}</Typography>
            </Grid>
          </Grid>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('certifications', 'Certifications')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('name', 'Name')}</TableCell>
                <TableCell>{t('issuingBody', 'Issuing body')}</TableCell>
                <TableCell>{t('expiryDate', 'Expiry date')}</TableCell>
                <TableCell>{t('status', 'Status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {profile.certifications.length === 0 && (
                <TableRow><TableCell colSpan={4}>{t('noCertifications', 'No certifications captured yet.')}</TableCell></TableRow>
              )}
              {profile.certifications.map((cert) => (
                <TableRow key={cert.id}>
                  <TableCell>{cert.name}</TableCell>
                  <TableCell>{cert.issuingBody}</TableCell>
                  <TableCell>{cert.expiryDate}</TableCell>
                  <TableCell><Chip label={cert.status}
                    color={DOCUMENT_STATUS_COLOR[cert.status]}
                    size="small" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('insurance', 'Insurance')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('type', 'Type')}</TableCell>
                <TableCell>{t('provider', 'Provider')}</TableCell>
                <TableCell>{t('coverAmount', 'Cover amount')}</TableCell>
                <TableCell>{t('expiryDate', 'Expiry date')}</TableCell>
                <TableCell>{t('status', 'Status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {profile.insurancePolicies.length === 0 && (
                <TableRow><TableCell colSpan={5}>{t('noInsurance', 'No insurance policies captured yet.')}</TableCell></TableRow>
              )}
              {profile.insurancePolicies.map((policy) => (
                <TableRow key={policy.id}>
                  <TableCell>{policy.type}</TableCell>
                  <TableCell>{policy.provider}</TableCell>
                  <TableCell>{policy.coverAmount.toLocaleString()}</TableCell>
                  <TableCell>{policy.expiryDate}</TableCell>
                  <TableCell><Chip label={policy.status}
                    color={DOCUMENT_STATUS_COLOR[policy.status]}
                    size="small" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('contractRenewal', 'Contract & renewal')}
          </Typography>
          {profile.contract ? (
            <Grid container
              spacing={2}>
              <Grid item
                xs={12}
                md={3}>
                <Typography variant="textXS"
                  color="text.secondary">{t('contractReference', 'Reference')}</Typography>
                <Typography variant="textSM">{profile.contract.contractReference}</Typography>
              </Grid>
              <Grid item
                xs={12}
                md={3}>
                <Typography variant="textXS"
                  color="text.secondary">{t('contractDates', 'Start / end')}</Typography>
                <Typography variant="textSM">{profile.contract.startDate} - {profile.contract.endDate}</Typography>
              </Grid>
              <Grid item
                xs={12}
                md={3}>
                <Typography variant="textXS"
                  color="text.secondary">{t('renewalNotice', 'Renewal notice period')}</Typography>
                <Typography variant="textSM">{profile.contract.renewalNoticePeriodDays} {t('days', 'days')}</Typography>
              </Grid>
              <Grid item
                xs={12}
                md={3}>
                <Typography variant="textXS"
                  color="text.secondary">{t('renewalStatus', 'Renewal status')}</Typography>
                <Chip label={profile.contract.renewalStatus}
                  color={RENEWAL_STATUS_COLOR[profile.contract.renewalStatus]}
                  size="small" />
              </Grid>
            </Grid>
          ) : (
            <Typography variant="textSM">{t('noContract', 'No contract captured yet.')}</Typography>
          )}
        </Box>
      </Section>
    </>
  );
};

export default DueDiligenceSection;
